import React, { useCallback, useState } from 'react';
import { DateTime } from 'luxon';

import {
  ReferenceManyField,
  Button,
  useCreate,
  useRedirect,
  Identifier,
  useResourceContext,
  useRecordContext,
  TextInput,
  required,
  useNotify,
  Form,
  useRefresh,
  usePermissions,
} from 'react-admin';

import {
  Grid,
  Button as LabelButton,
  Card,
  CardHeader,
  Divider,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardContent,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import Cancel from '@mui/icons-material/Cancel';
import MoreTime from '@mui/icons-material/MoreTime';
import { FieldValues } from 'react-hook-form';

import { CaseListBody } from '../components/case/CaseList';
import Pagination from '../components/layout/Pagination';

import entityTypes, { entityTypesFromResource } from '../constants/entityTypes';
import caseStatuses from '../constants/caseStatuses';
import SelectInputButton from '../components/layout/inputFields/SelectInputButton';
import useDialogStatus from '../hooks/useDialogStatus';
import SmartDateInput from './SmartDateInput';
import hasAccess from '../utilities/hasAccess';
import toStockholmTime from '../utilities/toStockholmTime';
import actions from '../constants/actions';
import PermissionMissing from '../components/utils/PermissionMissing';

const CreateButton = () => {
  const record = useRecordContext();

  const [create] = useCreate();
  const redirect = useRedirect();
  const resource = useResourceContext();

  const entityType = resource ? entityTypesFromResource[resource] : entityTypes.INDIVIDUAL;

  const handleClick = useCallback(() => {
    create(
      'cases',
      { data: { entityId: record?.id, entityType } },
      { onSuccess: ((resp: { id: Identifier }) => redirect('list', `cases/${resp.id}`)) },
    );
  }, [create, record?.id, redirect, entityType]);

  return (
    <Button label="Create" startIcon={<AddIcon />} onClick={handleClick} />
  );
};

const isFutureDate = (value: string) => {
  if (toStockholmTime(value).startOf('day') < toStockholmTime(DateTime.now()).startOf('day')) return 'Date should be in the future';
  return undefined;
};

const ScheduleModal = () => {
  const { open, openDialog, closeDialog } = useDialogStatus();

  const refresh = useRefresh();
  const record = useRecordContext();
  const resource = useResourceContext();
  const notify = useNotify();

  const [create] = useCreate();
  const handleSubmit = useCallback((data: FieldValues) => {
    if (!record || !resource) notify('Could not schedule case', { type: 'error' });
    else {
      create(
        'cases/schedule',
        {
          data: {
            entityId: record.id,
            entityType: entityTypesFromResource[resource],
            scheduledAt: toStockholmTime(data.scheduledAt).toISODate(),
            reason: data.reason,
          },
        },
        {
          onSuccess: () => {
            refresh();
            closeDialog();
          },
          onError: () => {
            notify('Could not schedule case', { type: 'error' });
          },
        },
      );
    }
  }, [closeDialog, create, notify, record, refresh, resource]);

  return (
    <>
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xs">
        <Form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight="bold">Schedule Case</Typography>
              <IconButton onClick={closeDialog}>
                <Cancel />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              display: 'flex',
              justifyContent: 'start',
            }}
          >
            <Grid container spacing={4}>
              <Grid size={12}>
                <SmartDateInput
                  helperText={false}
                  source="scheduledAt"
                  validate={[required(), isFutureDate]}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <TextInput
                  source="reason"
                  multiline
                  validate={required()}
                  margin="dense"
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <LabelButton onClick={closeDialog} color="secondary">
              Cancel
            </LabelButton>
            <LabelButton type="submit" variant="contained">
              Schedule
            </LabelButton>
          </DialogActions>
        </Form>
      </Dialog>
      <Button
        label="Schedule"
        startIcon={<MoreTime />}
        onClick={openDialog}
      />
    </>
  );
};

const LatestRelatedCases = () => {
  const [status, setStatus] = useState('all');
  const { permissions } = usePermissions();

  return (
    <Grid size={12}>
      <Card variant="outlined">
        <CardHeader
          title="Related cases"
          action={(
            <Box display="flex" gap={1}>
              <SelectInputButton
                choices={[
                  { id: 'active', name: 'Active cases only' },
                  { id: 'all', name: 'All cases' },
                ]}
                value={status}
                setValue={setStatus}
              />
              {hasAccess(permissions, actions.CASE_CREATE) && (
                <>
                  <CreateButton />
                  <ScheduleModal />
                </>
              )}
            </Box>
          )}
        />
        <Divider />
        {hasAccess(permissions, actions.CASE_LIST) ? (
          <ReferenceManyField
            perPage={5}
            pagination={<Pagination disableEmptyText rowsPerPageOptions={[5, 10, 20]} />}
            filter={status === 'all'
              ? {}
              : { status: [caseStatuses.IN_PROGRESS, caseStatuses.OPEN, caseStatuses.PENDING] }}
            reference="cases"
            target="entities"
            sort={{ field: 'createdAt', order: 'DESC' }}
          >
            <CaseListBody />
          </ReferenceManyField>
        ) : (
          <CardContent>
            <Box display="flex" alignItems="center" width="100%" flexDirection="column">
              <PermissionMissing />
            </Box>
          </CardContent>
        )}
      </Card>
    </Grid>
  );
};

export default LatestRelatedCases;
